/**
 * Подтверждение опасного действия.
 *
 * Одно на всю консоль, потому что правило одно: у КАЖДОЙ красной кнопки должен
 * быть вопрос. До сих пор он был ровно у одной из пяти, и остальные срабатывали
 * от случайного щелчка — отзыв ключа агента, остановка идущего прохода, отзыв
 * доступа. Владелец сказал прямо: боится нажимать. Это не придирка к
 * оформлению, это признак того, что интерфейс опасен.
 *
 * Три решения внутри, и каждое против случайного «да»:
 *
 *   1. Наведение по умолчанию на «Отмена». Enter, нажатый по привычке, отменяет,
 *      а не подтверждает.
 *   2. Escape закрывает. Уйти должно быть проще, чем согласиться.
 *   3. Для необратимого — ввод имени. Не для строгости: пока набираешь
 *      «docker.helsinki», успеваешь понять, ту ли машину отключаешь.
 *
 * Текст всегда говорит ПОСЛЕДСТВИЕ, а не действие. «Отозвать ключ?» — вопрос
 * ни о чём; «агент перестанет присылать копии, и чтобы вернуть его, придётся
 * регистрировать заново» — вопрос, на который можно ответить.
 */
import React, { useEffect, useRef, useState } from 'react';

export interface ConfirmProps {
  open: boolean;
  title: string;
  /** Что случится. Строки показываются абзацами. */
  consequences: string[];
  /** Что можно сделать вместо — если есть путь мягче. */
  instead?: string;
  confirmLabel: string;
  /** Слово, которое надо набрать. Задаётся только для необратимого. */
  typeToConfirm?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function Confirm({
  open, title, consequences, instead, confirmLabel, typeToConfirm, busy,
  onConfirm, onCancel,
}: ConfirmProps): React.JSX.Element | null {
  const [typed, setTyped] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setTyped('');
      return;
    }
    // Наведение на «Отмена», а не на «Подтвердить»: привычный Enter должен
    // уводить от беды, а не в неё.
    cancelRef.current?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const ready = !typeToConfirm || typed.trim() === typeToConfirm;

  return (
    <div className="modal" role="presentation" onClick={onCancel}>
      <div
        className="modal__box"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="modal__title" id="confirm-title">{title}</h3>

        <div className="modal__body">
          {consequences.map((line) => <p key={line} className="modal__line">{line}</p>)}
          {instead && <p className="modal__instead">{instead}</p>}
        </div>

        {typeToConfirm && (
          <label className="modal__type">
            <span className="modal__type-label">
              Наберите <b className="mono">{typeToConfirm}</b>, чтобы подтвердить
            </span>
            <input
              className="input"
              value={typed}
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        )}

        <div className="modal__actions">
          <button
            ref={cancelRef}
            type="button"
            className="button"
            disabled={busy}
            onClick={onCancel}
          >
            Отмена
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={busy || !ready}
            onClick={onConfirm}
          >
            {busy ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Состояние одного подтверждения на экран.
 *
 * Экрану нужно помнить, что именно подтверждают, — а не заводить по флагу на
 * каждую кнопку. `ask(...)` открывает вопрос и запоминает, что сделать по «да».
 */
export interface Pending {
  title: string;
  consequences: string[];
  instead?: string;
  confirmLabel: string;
  typeToConfirm?: string;
  run: () => void | Promise<void>;
}

export function useConfirm(): {
  pending: Pending | null;
  ask: (p: Pending) => void;
  close: () => void;
} {
  const [pending, setPending] = useState<Pending | null>(null);
  return {
    pending,
    ask: setPending,
    close: () => setPending(null),
  };
}
